import Phaser from 'phaser';
import { openInventory } from '../crafting/mode';
import { onGame, offGame, GameEvents } from '../events';
import { requestPanel } from '../navigation';
import { gameState } from '../state';
import { bottomMenuLayout, menuSelectionForPanel, menuTabTravel } from './bottom-menu-layout';
import {
  defaultBottomMenuVisibility,
  isMainInteractive,
  isSecondaryInteractive,
  mergeBottomMenuVisibility,
  type BottomMenuVisibility,
  type BottomMenuVisibilityPatch,
} from './bottom-menu-visibility';
import { currentGeodeType, menuGeodeTextureKey } from './menu-geode-icon';

type MenuButton = {
  key: keyof ReturnType<typeof bottomMenuLayout>['slots'];
  bgKey: string;
  iconKey: string;
  onTap: () => void;
};

const BUTTONS: MenuButton[] = [
  { key: 'rank', bgKey: 'menu-secondary-bg', iconKey: 'menu-rank', onTap: () => {} },
  { key: 'booga', bgKey: 'menu-secondary-bg', iconKey: 'menu-booga', onTap: () => requestPanel(0) },
  { key: 'main', bgKey: 'menu-main-bg', iconKey: 'menu-main', onTap: () => requestPanel(1) },
  { key: 'weapon', bgKey: 'menu-secondary-bg', iconKey: 'menu-weapon', onTap: () => openInventory('weapon') },
  { key: 'shop', bgKey: 'menu-secondary-bg', iconKey: 'menu-shop', onTap: () => {} },
];

function fitIcon(icon: Phaser.GameObjects.Image, bgW: number, scaleRatio: number) {
  const iconSize = bgW * scaleRatio;
  const iconScale = Math.min(iconSize / icon.width, iconSize / icon.height);
  icon.setScale(iconScale);
}

function applyAlpha(obj: Phaser.GameObjects.Components.Alpha & Phaser.GameObjects.Components.Visible, alpha: number) {
  obj.setAlpha(alpha);
  obj.setVisible(alpha > 0);
}

function setHitActive(hit: Phaser.GameObjects.Rectangle, active: boolean) {
  if (active) {
    if (!hit.input) hit.setInteractive({ useHandCursor: true });
    return;
  }
  hit.disableInteractive();
}

export function createBottomMenu(scene: Phaser.Scene) {
  const root = scene.add.container(0, 0).setScrollFactor(0).setDepth(110);

  const bar = scene.add.image(0, 0, 'menu-bar').setOrigin(0, 0).setDepth(0);
  root.add(bar);

  const buttonRoots: Phaser.GameObjects.Container[] = [];
  let mainGeodeIcon: Phaser.GameObjects.Image | null = null;
  let visibility = defaultBottomMenuVisibility();

  for (const button of BUTTONS) {
    const slotRoot = scene.add.container(0, 0).setDepth(1);
    const bg = scene.add.image(0, 0, button.bgKey).setOrigin(0.5, 1);

    const hit = scene.add
      .rectangle(0, 0, 1, 1, 0x000000, 0.001)
      .setOrigin(0.5, 0.5)
      .setInteractive({ useHandCursor: true });

    hit.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      pointer.event?.stopPropagation();
      button.onTap();
    });

    if (button.key === 'main') {
      const frameIcon = scene.add.image(0, 0, button.iconKey).setOrigin(0.5, 0.5);
      mainGeodeIcon = scene.add
        .image(0, 0, menuGeodeTextureKey(currentGeodeType()))
        .setOrigin(0.5, 0.5);
      slotRoot.add([bg, frameIcon, mainGeodeIcon, hit]);
    } else {
      const icon = scene.add.image(0, 0, button.iconKey).setOrigin(0.5, 0.5);
      slotRoot.add([bg, icon, hit]);
    }

    buttonRoots.push(slotRoot);
    root.add(slotRoot);
  }

  function setVisibility(patch: BottomMenuVisibilityPatch) {
    visibility = mergeBottomMenuVisibility(visibility, patch);
    relayout();
  }

  function getVisibility(): BottomMenuVisibility {
    return visibility;
  }

  function refreshGeodeIcon() {
    if (!mainGeodeIcon) return;
    mainGeodeIcon.setTexture(menuGeodeTextureKey(currentGeodeType()));
    relayout();
  }

  const onGeodeChanged = () => refreshGeodeIcon();
  onGame(GameEvents.GEODE_SYNC, onGeodeChanged);
  onGame(GameEvents.GEODE_RESPAWN, onGeodeChanged);
  onGame(GameEvents.PANEL_CHANGED, relayout);

  function relayout() {
    const layout = bottomMenuLayout();
    const selectedKey = menuSelectionForPanel(gameState.currentPanel);
    const travel = menuTabTravel();
    let selectedRoot: Phaser.GameObjects.Container | null = null;

    applyAlpha(bar, visibility.bar);
    bar
      .setPosition(layout.barX, layout.barY)
      .setDisplaySize(layout.barW, layout.barH);
    root.sendToBack(bar);

    BUTTONS.forEach((button, index) => {
      const slot = layout.slots[button.key];
      const slotRoot = buttonRoots[index];
      if (!slot || !slotRoot) return;

      const isMain = button.key === 'main';
      const isSelected = button.key === selectedKey;
      const bgW = isMain ? layout.mainW : layout.secondaryW;
      const bgH = isMain ? layout.mainH : layout.secondaryH;

      const y = slot.y + (isSelected ? 0 : travel);
      slotRoot.setPosition(slot.x, y);
      slotRoot.setVisible(true);
      if (isSelected) selectedRoot = slotRoot;

      const iconY = slot.iconY - slot.y;

      if (isMain) {
        const [bg, frameIcon, geodeIcon, hit] = slotRoot.list as [
          Phaser.GameObjects.Image,
          Phaser.GameObjects.Image,
          Phaser.GameObjects.Image,
          Phaser.GameObjects.Rectangle,
        ];
        const parts = visibility.main;

        bg.setDisplaySize(bgW, bgH);
        frameIcon.setPosition(0, iconY);
        geodeIcon.setPosition(0, iconY);
        fitIcon(frameIcon, bgW, slot.iconScale);
        fitIcon(geodeIcon, bgW, slot.geodeIconScale ?? slot.iconScale);
        applyAlpha(bg, parts.bg);
        applyAlpha(frameIcon, parts.frame);
        applyAlpha(geodeIcon, parts.geode);
        hit.setPosition(0, -bgH * 0.5).setSize(bgW * 0.78, bgH * 0.78);
        setHitActive(hit, isMainInteractive(parts));
        return;
      }

      const [bg, icon, hit] = slotRoot.list as [
        Phaser.GameObjects.Image,
        Phaser.GameObjects.Image,
        Phaser.GameObjects.Rectangle,
      ];
      const parts = visibility[button.key as 'rank' | 'booga' | 'weapon' | 'shop'];

      bg.setDisplaySize(bgW, bgH);
      icon.setPosition(0, iconY);
      fitIcon(icon, bgW, slot.iconScale);
      applyAlpha(bg, parts.bg);
      applyAlpha(icon, parts.icon);
      hit.setPosition(0, -bgH * 0.5).setSize(bgW * 0.78, bgH * 0.78);
      setHitActive(hit, isSecondaryInteractive(parts));
    });

    if (selectedRoot) root.bringToTop(selectedRoot);
  }

  relayout();

  return {
    root,
    relayout,
    refreshGeodeIcon,
    setVisibility,
    getVisibility,
    destroy() {
      offGame(GameEvents.GEODE_SYNC, onGeodeChanged);
      offGame(GameEvents.GEODE_RESPAWN, onGeodeChanged);
      offGame(GameEvents.PANEL_CHANGED, relayout);
    },
  };
}
